import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Tooltip,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import InventoryIcon from "@mui/icons-material/Inventory";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SensorsIcon from "@mui/icons-material/Sensors";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useConnectorHealth } from "../utils/useConnectorHealth";

type Props = {
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  onClose?: () => void;
  drawerWidth?: number;
  sx?: any;
};

/** Shown on pages that exist but are not agreed on yet. */
const CONCEPT_TOOLTIP = "Concept - Need to discuss";

type NavItemProps = {
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  /** Greys the entry out and explains why on hover. */
  disabled?: boolean;
  /** Trailing adornment, e.g. the connector health badge. */
  adornment?: React.ReactNode;
};

const NavItem: React.FC<NavItemProps> = ({
  label,
  icon,
  selected,
  onClick,
  disabled = false,
  adornment,
}) => {
  const button = (
    <ListItemButton selected={selected} onClick={onClick} disabled={disabled}>
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <ListItemText primary={label} slotProps={{ primary: { fontWeight: 500 } }} />
      {adornment}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      {disabled ? (
        // A disabled button swallows pointer events, so the tooltip needs a
        // live wrapper to hang off — otherwise it never shows.
        <Tooltip title={CONCEPT_TOOLTIP}>
          <Box component="span" sx={{ width: "100%" }}>
            {button}
          </Box>
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
};

const MainSideBar: React.FC<Props> = ({
  variant = "permanent",
  open = false,
  onClose,
  drawerWidth = 240,
  sx,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAdminAccess } = useAuth();

  // Only admins and managers have the Connectors page to click through to,
  // so only they carry the badge — and only they pay for the poll.
  const { offline, degraded } = useConnectorHealth(hasAdminAccess);
  const troubled = offline.length ? offline : degraded;
  const connectorAlert = troubled.length
    ? {
        // A dead connector is red; a lagging one is amber. Both mean the
        // dashboards above are showing data that stopped updating.
        down: offline.length > 0,
        text: `${troubled.map((c) => c.name).join(", ")} ${
          troubled.length === 1 ? "is" : "are"
        } ${offline.length ? "offline" : "degraded"}`,
      }
    : null;

  const go = (to: string) => () => {
    navigate(to);
    if (variant === "temporary" && onClose) onClose();
  };

  // Slightly wider on temporary (mobile) for finger-tap comfort
  const paperWidth = variant === "temporary" ? 260 : drawerWidth;

  return (
    <Drawer
      variant={variant}
      open={variant === "permanent" ? true : open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: paperWidth,
          boxSizing: "border-box",
          borderRight: (t) => `1px solid ${t.palette.divider}`,
        },
        ...sx,
      }}
    >
      {/* Spacer matches AppBar height. The AppBar sits above the drawer
          (zIndex drawer + 1), so both variants need it — without it the first
          nav item hides under the bar on mobile. */}
      <Toolbar />
      <Divider />
      <List sx={{ py: 1 }}>
        {hasAdminAccess && (
          <NavItem
            label="Main Dashboard"
            icon={<DashboardIcon />}
            selected={location.pathname === "/main"}
            onClick={go("/main")}
          />
        )}
        {hasAdminAccess && (
          <NavItem
            label="Reports"
            icon={<AssessmentIcon />}
            selected={location.pathname === "/reports"}
            onClick={go("/reports")}
          />
        )}
        {hasAdminAccess && (
          <NavItem
            label="Alerts"
            icon={<NotificationsActiveIcon />}
            selected={location.pathname === "/alerts"}
            onClick={go("/alerts")}
            disabled
          />
        )}
        {/* Parked pending a decision — the pages still exist and the routes
            still resolve; only the way in from the nav is closed. */}
        {hasAdminAccess && (
          <NavItem
            label="Sales Orders"
            icon={<LocalShippingIcon />}
            selected={location.pathname === "/sales-orders"}
            onClick={go("/sales-orders")}
            disabled
          />
        )}
        {hasAdminAccess && (
          <NavItem
            label="Manufacturing Orders"
            icon={<AssignmentIcon />}
            selected={location.pathname === "/manufacturing"}
            onClick={go("/manufacturing")}
            disabled
          />
        )}
        {hasAdminAccess && (
          <NavItem
            label="Inventory"
            icon={<InventoryIcon />}
            selected={location.pathname === "/inventory"}
            onClick={go("/inventory")}
            disabled
          />
        )}
        <NavItem
          label="Lamination Line 1"
          icon={<PrecisionManufacturingIcon />}
          selected={location.pathname === "/lamination1"}
          onClick={go("/lamination1")}
        />
        <NavItem
          label="Lamination Line 2"
          icon={<PrecisionManufacturingIcon />}
          selected={location.pathname === "/lamination2"}
          onClick={go("/lamination2")}
        />
        <NavItem
          label="Foam Line"
          icon={<PrecisionManufacturingIcon />}
          selected={location.pathname === "/foam"}
          onClick={go("/foam")}
        />
        {hasAdminAccess && (
          <NavItem
            label="Connectors"
            icon={<SensorsIcon />}
            selected={location.pathname === "/connectors"}
            onClick={go("/connectors")}
            adornment={
              connectorAlert && (
                <Tooltip title={connectorAlert.text}>
                  {/* Announced as text too: a color alone is not a status. */}
                  <Box
                    component="span"
                    role="status"
                    aria-label={connectorAlert.text}
                    sx={{ display: "flex", alignItems: "center", ml: 1 }}
                  >
                    {connectorAlert.down ? (
                      <ErrorIcon color="error" fontSize="small" />
                    ) : (
                      <WarningAmberIcon color="warning" fontSize="small" />
                    )}
                  </Box>
                </Tooltip>
              )
            }
          />
        )}
      </List>
    </Drawer>
  );
};

export default MainSideBar;
